import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format, parse, isValid } from 'date-fns';
import { it } from 'date-fns/locale';
import { apiPost, apiGet } from '../../api/client';
import type { Client, Event } from '../../api/types';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { formatEventType } from '../../utils/format';

function formatDisplayDate(isoDate: string): string {
  if (!isoDate) return '';
  const d = new Date(isoDate + 'T00:00:00');
  if (!isValid(d)) return isoDate;
  return format(d, 'dd/MM/yyyy', { locale: it });
}

function parseDisplayDate(text: string): string {
  if (!text) return '';
  const d = parse(text, 'dd/MM/yyyy', new Date());
  if (!isValid(d)) return text;
  return format(d, 'yyyy-MM-dd');
}

interface Props { open: boolean; onClose: () => void; }

const EVENT_TYPES = ['MATRIMONIO','BATTESIMO','COMUNIONE','CRESIMA','COMPLEANNO','RITRATTO','ALTRO'];

export default function NewEventModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', eventType: 'MATRIMONIO', eventDate: '', startTime: '', endTime: '', clientId: '',
    location: '', notes: '', syncToGoogle: false,
    subject1Name: '', subject2Name: '',
  });

  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ['clients-list'],
    queryFn: async () => {
      const res = await apiGet<Client[]>('/clients?limit=200');
      return res ?? [];
    },
    enabled: open,
  });

  const dateValid = !!(form.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(form.eventDate) && !isNaN(new Date(form.eventDate).getTime()));

  // Live conflict check: fires whenever the date field changes
  const { data: conflictEvent } = useQuery<Event | null>({
    queryKey: ['event-date-conflict', form.eventDate],
    queryFn: async () => {
      const start = encodeURIComponent(`${form.eventDate}T00:00:00.000Z`);
      const end   = encodeURIComponent(`${form.eventDate}T23:59:59.999Z`);
      const res = await apiGet<{ data: Event[] }>(`/events?dateFrom=${start}&dateTo=${end}&limit=1`);
      const events = (res as unknown as { data: Event[] }).data ?? (res as unknown as Event[]);
      return (events[0] as Event) ?? null;
    },
    enabled: dateValid,
    staleTime: 15_000,
  });

  const mutation = useMutation({
    mutationFn: () => {
      if (!form.title.trim()) throw new Error('Il titolo è obbligatorio');
      if (!dateValid) throw new Error('La data non è valida');
      if (!form.clientId) throw new Error('Seleziona un cliente');
      return apiPost<Event>('/events', {
        title: form.title,
        eventType: form.eventType,
        eventDate: new Date(form.eventDate).toISOString(),
        startTime: form.startTime || undefined,
        endTime: form.endTime || undefined,
        clientId: form.clientId,
        location: form.location || undefined,
        notes: form.notes || undefined,
        syncToGoogle: form.syncToGoogle,
        subject1Name: form.subject1Name || undefined,
        subject2Name: form.subject2Name || undefined,
      });
    },
    onSuccess: (ev) => {
      qc.invalidateQueries({ queryKey: ['events'] });
      toast.success('Evento creato');
      onClose();
      navigate(`/events/${ev.id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); mutation.mutate(); };

  const set = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nuovo evento"
      width={540}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>Crea evento</Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <Input label="Titolo evento *" value={form.title} onChange={e => set('title', e.target.value)} required placeholder="es. Matrimonio Rossi-Ferrari" />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <Select
            label="Tipo *"
            value={form.eventType}
            onChange={e => set('eventType', e.target.value)}
            options={EVENT_TYPES.map(t => ({ value: t, label: formatEventType(t) }))}
          />
          <Input label="Data *" type="text" inputMode="numeric" placeholder="GG/MM/AAAA" value={formatDisplayDate(form.eventDate)} onChange={e => set('eventDate', parseDisplayDate(e.target.value))} required />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <Input label="Orario inizio" type="text" inputMode="numeric" placeholder="HH:MM" pattern="[0-2][0-9]:[0-5][0-9]" value={form.startTime} onChange={e => set('startTime', e.target.value)} />
          <Input label="Orario fine" type="text" inputMode="numeric" placeholder="HH:MM" pattern="[0-2][0-9]:[0-5][0-9]" value={form.endTime} onChange={e => set('endTime', e.target.value)} />
        </div>

        {conflictEvent && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
            background: 'var(--color-warning-highlight)',
            border: '1px solid var(--color-warning)',
            borderRadius: 'var(--radius-card)',
            padding: '0.75rem',
          }}>
            <AlertTriangle size={15} style={{ color: 'var(--color-warning)', flexShrink: 0, marginTop: '0.1rem' }} />
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', lineHeight: 1.5 }}>
              <strong>Attenzione:</strong> hai già un evento in questa data —{' '}
              <strong>{conflictEvent.title}</strong> ({formatEventType(conflictEvent.eventType)}).{' '}
              Vuoi procedere comunque?
            </p>
          </div>
        )}

        <Select
          label="Cliente *"
          value={form.clientId}
          onChange={e => set('clientId', e.target.value)}
          placeholder="Seleziona cliente"
          options={clients.map(c => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))}
        />

        <Input label="Location" value={form.location} onChange={e => set('location', e.target.value)} placeholder="Villa, chiesa, studio…" />

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.syncToGoogle} onChange={e => setForm(f => ({ ...f, syncToGoogle: e.target.checked }))} />
          Sincronizza con Google Calendar
        </label>

        {form.eventType === 'MATRIMONIO' ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Sposo" value={form.subject1Name} onChange={e => set('subject1Name', e.target.value)} placeholder="Nome dello sposo" />
            <Input label="Sposa" value={form.subject2Name} onChange={e => set('subject2Name', e.target.value)} placeholder="Nome della sposa" />
          </div>
        ) : (
          <Input label="Festeggiato/a" value={form.subject1Name} onChange={e => set('subject1Name', e.target.value)} placeholder="Nome del festeggiato/a" />
        )}
      </form>
    </Modal>
  );
}
